import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { UserCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    capital_deployment: '',
    vehicle: ''
  });

  useEffect(() => {
    // Check if user is logged in and approved
    const email = localStorage.getItem('user_email');
    const status = localStorage.getItem('user_status');
    
    if (!email) {
      navigate('/login');
      return;
    }
    
    if (status === 'rejected') {
      navigate('/login');
      return;
    }

    // Fetch user data to prefill phone
    fetchUserData(email);
  }, [navigate]);

  const fetchUserData = async (email) => {
    try {
      const response = await axios.get(`${API}/profile?email=${email}`);
      setFormData(prev => ({
        ...prev,
        phone: response.data.phone || ''
      }));
    } catch (error) {
      console.error('Failed to fetch user data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.phone || !formData.capital_deployment || !formData.vehicle) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const email = localStorage.getItem('user_email');
      await axios.post(`${API}/auth/onboarding?email=${email}`, {
        ...formData,
        capital_deployment: parseInt(formData.capital_deployment)
      });
      
      toast.success('Profile completed!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to complete onboarding';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <Card className="border-[#E2E8F0] shadow-none">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2">
              <UserCircle className="h-6 w-6 text-brand" />
              <CardTitle className="font-heading text-2xl font-bold text-[#0F172A]">
                COMPLETE YOUR PROFILE
              </CardTitle>
            </div>
            <CardDescription className="text-[#64748B]">
              Tell us about yourself to access deal opportunities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-[#0F172A] font-medium text-sm">First Name *</Label>
                  <Input
                    data-testid="first-name-input"
                    id="firstName"
                    type="text"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="bg-white border-[#E2E8F0] h-11 text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-[#0F172A] font-medium text-sm">Last Name *</Label>
                  <Input
                    data-testid="last-name-input"
                    id="lastName"
                    type="text"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="bg-white border-[#E2E8F0] h-11 text-base"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#0F172A] font-medium text-sm">Phone Number *</Label>
                <Input
                  data-testid="phone-input"
                  id="phone"
                  type="tel"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-white border-[#E2E8F0] h-11 text-base"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="capital" className="text-[#0F172A] font-medium text-sm">Capital Deployment / Year (INR) *</Label>
                <Input
                  data-testid="capital-input"
                  id="capital"
                  type="number"
                  placeholder="e.g., 5000000"
                  value={formData.capital_deployment}
                  onChange={(e) => setFormData({...formData, capital_deployment: e.target.value})}
                  className="bg-white border-[#E2E8F0] h-11 text-base"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicle" className="text-[#0F172A] font-medium text-sm">Investment Vehicle *</Label>
                <Select
                  value={formData.vehicle}
                  onValueChange={(value) => setFormData({...formData, vehicle: value})}
                >
                  <SelectTrigger data-testid="vehicle-select" className="bg-white border-[#E2E8F0] h-11">
                    <SelectValue placeholder="Select vehicle type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0]">
                    <SelectItem value="Vehicle Type 1">Vehicle Type 1</SelectItem>
                    <SelectItem value="Vehicle Type 2">Vehicle Type 2</SelectItem>
                    <SelectItem value="Vehicle Type 3">Vehicle Type 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                data-testid="onboarding-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#10B981] hover:bg-[#059669] text-white h-12 font-semibold rounded-md text-base mt-6"
              >
                {loading ? 'Saving...' : 'Complete Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
